import { strFromU8, unzipSync } from 'fflate';

import { extractDocxText } from '@/lib/docx-text';
import { readUriAsBase64 } from '@/lib/files';

/**
 * Universal document text previews (Wave 56.1) — the engine behind
 * Drive-style "page" thumbnails for every text-bearing file type.
 * Everything runs on-device: Office formats are zips of XML, the rest
 * are text already. The extracted text is rendered onto a white
 * "paper" tile by DocPagePreview; no server, no API, works offline.
 *
 * Legacy binary formats (.doc, .xls, .ppt) are NOT parseable here —
 * they keep the icon tile.
 */

const cache = new Map<string, string | null>();

const TEXTY_EXT =
  /\.(txt|csv|tsv|json|xml|md|log|ini|yml|yaml|ics|eml|html?|rtf)$/i;

export function isTextPreviewable(
  mime: string | null | undefined,
  name: string | null | undefined
): boolean {
  const n = name ?? '';
  if (/\.(docx|xlsx|pptx)$/i.test(n)) return true;
  if (TEXTY_EXT.test(n)) return true;
  const m = mime ?? '';
  if (m.includes('wordprocessingml') || m.includes('spreadsheetml') || m.includes('presentationml')) return true;
  if (m.startsWith('text/')) return true;
  if (m === 'application/json' || m === 'application/xml' || m === 'message/rfc822' || m === 'application/rtf') return true;
  return false;
}

function base64ToBytes(base64: string): Uint8Array {
  // eslint-disable-next-line no-undef
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

function stripXml(xml: string, paraTag: string): string {
  return xml
    .replace(new RegExp(`</${paraTag}>`, 'g'), '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&#(\d+);/g, (_, d) => String.fromCharCode(Number(d)))
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function extractXlsxText(bytes: Uint8Array): string | null {
  try {
    const files = unzipSync(bytes);
    const out: string[] = [];
    const shared = files['xl/sharedStrings.xml'];
    if (shared) {
      const xml = strFromU8(shared);
      const strings = [...xml.matchAll(/<t[^>]*>([^<]*)<\/t>/g)].map((m) => m[1]);
      // Group into pseudo-rows of a few cells for a table-ish look.
      for (let i = 0; i < Math.min(strings.length, 120); i += 4) {
        out.push(strings.slice(i, i + 4).join('   '));
      }
    }
    return out.length > 0 ? out.join('\n') : null;
  } catch {
    return null;
  }
}

function extractPptxText(bytes: Uint8Array): string | null {
  try {
    const files = unzipSync(bytes);
    const out: string[] = [];
    for (let i = 1; i <= 8; i++) {
      const slide = files[`ppt/slides/slide${i}.xml`];
      if (!slide) break;
      const text = stripXml(strFromU8(slide), 'a:p');
      if (text) out.push(text);
    }
    return out.length > 0 ? out.join('\n\n— — —\n\n') : null;
  } catch {
    return null;
  }
}

function stripRtf(raw: string): string {
  return raw
    .replace(/\\par[d]?/g, '\n')
    .replace(/\\'[0-9a-f]{2}/gi, ' ')
    .replace(/\\[a-z]+-?\d*\s?/gi, '')
    .replace(/[{}]/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function stripHtml(raw: string): string {
  return raw
    .replace(/<(style|script)[\s\S]*?<\/\1>/gi, '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|tr|li|h[1-6])>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** Extract preview text for a file. Cached per URI; null = not
 *  extractable (caller shows the icon tile). */
export async function getDocPreviewText(
  uri: string,
  mime: string | null | undefined,
  name: string | null | undefined
): Promise<string | null> {
  if (cache.has(uri)) return cache.get(uri) ?? null;
  let text: string | null = null;
  try {
    const base64 = await readUriAsBase64(uri);
    if (base64) {
      const n = (name ?? '').toLowerCase();
      const m = mime ?? '';
      if (/\.docx$/.test(n) || m.includes('wordprocessingml')) {
        text = extractDocxText(base64);
      } else if (/\.xlsx$/.test(n) || m.includes('spreadsheetml')) {
        text = extractXlsxText(base64ToBytes(base64));
      } else if (/\.pptx$/.test(n) || m.includes('presentationml')) {
        text = extractPptxText(base64ToBytes(base64));
      } else {
        const raw = strFromU8(base64ToBytes(base64));
        if (/\.rtf$/.test(n) || m === 'application/rtf') text = stripRtf(raw);
        else if (/\.(html?|eml)$/.test(n) || m.includes('html') || m === 'message/rfc822') text = stripHtml(raw);
        else text = raw.trim();
      }
    }
  } catch {
    text = null;
  }
  if (text) {
    text = text.slice(0, 4000);
    if (text.replace(/\s/g, '').length < 12) text = null;
    // Binary sniff: a misidentified binary decodes into control chars
    // and replacement glyphs — never render that as a "page".
    if (text) {
      let junk = 0;
      for (let k = 0; k < text.length; k++) {
        const c = text.charCodeAt(k);
        if ((c < 32 && c !== 9 && c !== 10 && c !== 13) || c === 0xfffd) junk++;
      }
      if (junk / text.length > 0.05) text = null;
    }
  }
  cache.set(uri, text);
  return text;
}
